import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { fmt, fmtInt } from "@/lib/format";
import {
  Users as UsersIcon, CalendarDays, Wallet, Package as PackageIcon,
  TrendingUp, AlertTriangle, Cake, Activity, ArrowRight, ArrowLeft,
  ChevronLeft, ChevronRight, Plus, Receipt, FileBarChart, UserPlus,
  Sparkles, Crown, Award, Scissors, Clock, Phone, Star, Trophy,
  Building2, ArrowRightLeft, Zap,
} from "lucide-react";
import { toast } from "sonner";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";
import { AreaChart, Area, XAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

// =================== helpers ===================
const monthName = (d, lang) =>
  d.toLocaleDateString(lang === "ar" ? "ar-QA" : "en-GB", { month: "long", year: "numeric" });

const timeAgo = (iso, lang) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diffM = Math.floor((Date.now() - d.getTime()) / 60000);
  if (diffM < 1) return lang === "ar" ? "الآن" : "now";
  if (diffM < 60) return lang === "ar" ? `قبل ${diffM} د` : `${diffM}m ago`;
  const h = Math.floor(diffM / 60);
  if (h < 24) return lang === "ar" ? `قبل ${h} س` : `${h}h ago`;
  const days = Math.floor(h / 24);
  return lang === "ar" ? `قبل ${days} يوم` : `${days}d ago`;
};

const initials = (name) => (name || "?").trim().slice(0, 1).toUpperCase();

// =================== Section header ===================
const SectionHead = ({ icon: Icon, color, title, count, action, onClick, testId }) => {
  const palettes = {
    teal:    "bg-emerald-100 text-emerald-700",
    amber:   "bg-amber-100 text-amber-700",
    violet:  "bg-violet-100 text-violet-700",
    rose:    "bg-rose-100 text-rose-700",
    sky:     "bg-sky-100 text-sky-700",
    pink:    "bg-pink-100 text-pink-700",
    indigo:  "bg-indigo-100 text-indigo-700",
    gold:    "bg-amber-100 text-amber-800",
  };
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2.5">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${palettes[color] || palettes.teal}`}>
          <Icon size={17}/>
        </div>
        <h2 className="font-heading text-base text-[var(--text)]" data-testid={testId}>{title}</h2>
        {count != null && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[var(--bg)] text-[var(--text-muted)] num-display border border-[var(--border-subtle)]">{count}</span>}
      </div>
      {action && (
        <button onClick={onClick} className="text-xs gold-text hover:underline flex items-center gap-1">
          {action}
        </button>
      )}
    </div>
  );
};

const TOP_STAFF_MEDALS = ["bg-amber-400", "bg-gray-300", "bg-amber-700"];

const TopStaffRankRows = ({ list, mode, lang, fmt, emptyMsg }) => (
  <div className="space-y-2.5">
    {(list || []).map((s, i) => {
      const medal = TOP_STAFF_MEDALS[i] || "bg-[var(--bg)]";
      return (
        <div key={`${s.user_id}-${mode}-${i}`} className="flex items-center gap-3 p-1.5 rounded-lg hover:bg-[var(--bg)]">
          <div className={`w-7 h-7 rounded-full ${medal} text-white flex items-center justify-center font-bold text-xs shadow-sm`}>{i + 1}</div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-[var(--text)] truncate">{s.name}</div>
            {mode === "revenue" ? (
              <div className="text-[10px] text-[var(--text-muted)]">{s.bookings} {lang === "ar" ? "حجز" : "bookings"}</div>
            ) : (
              <div className="text-[10px] text-[var(--text-muted)] num-display">{fmt(s.revenue)}</div>
            )}
          </div>
          {mode === "revenue" ? (
            <div className="gold-text font-bold text-sm num-display">{fmt(s.revenue)}</div>
          ) : (
            <div className="font-bold text-sm num-display text-[var(--text)]">{s.bookings}</div>
          )}
        </div>
      );
    })}
    {(!list || list.length === 0) && (
      <div className="text-sm text-[var(--text-muted)] text-center py-3 flex flex-col items-center gap-2">
        <Award size={18} className="opacity-40"/>
        <span>{emptyMsg}</span>
      </div>
    )}
  </div>
);

// =================== KPI card (richer than v1) ===================
const KpiCard = ({ icon: Icon, label, value, sub, color = "teal", testId }) => {
  const palette = {
    teal:   { ic: "from-emerald-400 to-emerald-600", text: "text-emerald-600" },
    amber:  { ic: "from-amber-400 to-orange-500",    text: "text-amber-600" },
    indigo: { ic: "from-indigo-400 to-indigo-600",   text: "text-indigo-600" },
    rose:   { ic: "from-rose-400 to-rose-600",       text: "text-rose-600" },
    violet: { ic: "from-violet-400 to-violet-600",   text: "text-violet-600" },
    sky:    { ic: "from-sky-400 to-sky-600",         text: "text-sky-600" },
  }[color] || {};
  return (
    <div data-testid={testId}
      className="group relative overflow-hidden p-5 rounded-2xl bg-white border border-[var(--border)] shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all">
      {/* subtle decorative blob */}
      <div className={`absolute -end-6 -top-6 w-24 h-24 rounded-full bg-gradient-to-br ${palette.ic} opacity-10 group-hover:opacity-20 transition-opacity`}/>
      <div className="flex items-start gap-4 relative">
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${palette.ic} text-white flex items-center justify-center shrink-0 shadow-md`}>
          <Icon size={22}/>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-3xl font-bold leading-tight text-[var(--text)] num-display">{value}</div>
          <div className="text-xs text-[var(--text-muted)] truncate mt-0.5">{label}</div>
          {sub && <div className={`text-[10px] mt-1.5 font-medium ${palette.text}`}>{sub}</div>}
        </div>
      </div>
    </div>
  );
};

// =================== Quick action button ===================
const QuickAction = ({ icon: Icon, label, onClick, tone = "emerald", testId }) => {
  const tones = {
    emerald: "from-emerald-500 to-emerald-600 hover:shadow-emerald-200",
    sky:     "from-sky-500 to-sky-600 hover:shadow-sky-200",
    violet:  "from-violet-500 to-violet-600 hover:shadow-violet-200",
    rose:    "from-rose-500 to-rose-600 hover:shadow-rose-200",
  };
  return (
    <button onClick={onClick} data-testid={testId}
      className={`flex flex-col items-center justify-center gap-1.5 p-3.5 rounded-xl bg-gradient-to-br ${tones[tone]} text-white text-xs font-semibold shadow-md hover:shadow-xl transition-all hover:-translate-y-0.5`}>
      <Icon size={20}/>
      <span>{label}</span>
    </button>
  );
};

// =================== Mini calendar ===================
const MiniCalendar = ({ events = [], lang }) => {
  const [cursor, setCursor] = useState(() => new Date());
  const today = new Date();
  const firstDay = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const startOffset = firstDay.getDay();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const dotsByDay = new Map();
  for (const ev of events) {
    const d = new Date(ev.date);
    if (d.getMonth() === cursor.getMonth() && d.getFullYear() === cursor.getFullYear()) {
      const key = d.getDate();
      dotsByDay.set(key, [...(dotsByDay.get(key) || []), ev.color || "#7A1F3D"]);
    }
  }
  const weekdayLabels = lang === "ar" ? ["أحد", "إث", "ثل", "أر", "خم", "جم", "سب"] : ["S", "M", "T", "W", "T", "F", "S"];
  return (
    <div className="card-surface p-4" data-testid="mini-calendar">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1">
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="p-1.5 rounded hover:bg-[var(--bg)] text-[var(--text-muted)]" data-testid="cal-prev">
            <ChevronRight size={14}/>
          </button>
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="p-1.5 rounded hover:bg-[var(--bg)] text-[var(--text-muted)]" data-testid="cal-next">
            <ChevronLeft size={14}/>
          </button>
        </div>
        <div className="font-heading text-base text-[var(--text)] num-display">{monthName(cursor, lang)}</div>
        <CalendarDays size={16} className="text-[var(--gold)]"/>
      </div>
      <div className="grid grid-cols-7 gap-1 text-[10px] text-[var(--text-muted)] mb-1 text-center font-medium">
        {weekdayLabels.map((w, i) => <div key={i}>{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {cells.map((day, i) => {
          if (!day) return <div key={i} className="h-9"/>;
          const isToday = day === today.getDate() && cursor.getMonth() === today.getMonth() && cursor.getFullYear() === today.getFullYear();
          const dots = dotsByDay.get(day) || [];
          return (
            <div key={i} className={`h-9 rounded-md flex flex-col items-center justify-center text-xs relative ${isToday ? "bg-gradient-to-br from-[var(--gold)] to-[var(--rose)] text-white font-bold shadow-md" : "text-[var(--text)] hover:bg-[var(--bg)]"}`}>
              <span className="num-display">{day}</span>
              {dots.length > 0 && (
                <div className="absolute bottom-0.5 flex gap-0.5">
                  {dots.slice(0, 3).map((c, k) => <span key={k} className="w-1 h-1 rounded-full" style={{ background: c }}/>)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// =================== MAIN ===================
export default function Dashboard() {
  const { t, lang } = useLanguage();
  const { confirm, ConfirmDialogSlot } = useConfirmDialog();
  const { user, canAccessPage } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [executing, setExecuting] = useState(null); // product_id currently being transferred
  const [entitlementBusy, setEntitlementBusy] = useState(null);
  const [rescheduleDates, setRescheduleDates] = useState({});
  const [serveTick, setServeTick] = useState(0);

  const pendingPackages = data?.pending_package_entitlements || [];

  const nowServing = data?.now_serving || [];

  useEffect(() => {
    if (!nowServing.length) return undefined;
    const iv = setInterval(() => setServeTick((x) => x + 1), 30000);
    return () => clearInterval(iv);
  }, [nowServing.length]);

  const liveTotalMinutes = useCallback((row) => {
    if (!row?.arrived_at) return row?.live_total_minutes ?? 0;
    void serveTick;
    return Math.max(0, Math.round((Date.now() - new Date(row.arrived_at).getTime()) / 60000));
  }, [serveTick]);

  const sessionStateLabel = useCallback((state) => {
    if (state === "waiting") return t("waiting");
    if (state === "in_service") return t("in_service");
    return t("in_progress");
  }, [t]);

  const bookingStatusBadge = useCallback((b) => {
    const st = b.display_status || b.status || "confirmed";
    if (st === "completed" || b.invoiced || b.paid_at) {
      return { label: lang === "ar" ? "مكتمل" : "Completed", cls: "bg-emerald-100 text-emerald-800" };
    }
    if (st === "pending") return { label: lang === "ar" ? "معلق" : "Pending", cls: "bg-amber-100 text-amber-800" };
    if (st === "confirmed") return { label: lang === "ar" ? "مؤكد" : "Confirmed", cls: "bg-blue-100 text-blue-800" };
    return { label: st, cls: "bg-stone-100 text-stone-700" };
  }, [lang]);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/dashboard/overview"); setData(r.data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const patchEntitlement = async (entitlementId, body) => {
    setEntitlementBusy(entitlementId);
    try {
      await api.patch(`/package-entitlements/${entitlementId}`, body);
      toast.success(lang === "ar" ? "تم التحديث" : "Updated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang === "ar" ? "تعذر التحديث" : "Update failed"));
    } finally {
      setEntitlementBusy(null);
    }
  };

  const confirmRedeemEntitlement = async (entitlementId) => {
    const ok = await confirm({
      title: lang === "ar" ? "تأكيد إتمام الزيارة؟" : "Mark this visit as done?",
      description: lang === "ar"
        ? "سيتم تسجيل خدمة الباقة كمُستَلَمة. لا يمكن التراجع بسهولة."
        : "This will mark the package visit as redeemed.",
      confirmLabel: lang === "ar" ? "تمت" : "Done",
      cancelLabel: lang === "ar" ? "إلغاء" : "Cancel",
      variant: "primary",
    });
    if (!ok) return;
    await patchEntitlement(entitlementId, { status: "redeemed" });
  };

  // Load smart transfer suggestions in parallel. Endpoint is gated by the
  // stock-movements plan-page; tenants on plans that exclude it would get a
  // 403 — we skip the call entirely so the DevTools network panel stays
  // clean and we don't waste a round-trip on a result we'll throw away.
  const loadSuggestions = useCallback(async () => {
    if (!canAccessPage("stock-movements")) { setSuggestions([]); return; }
    try {
      const r = await api.get("/inventory-transfers/suggest?limit=8");
      setSuggestions(r.data || []);
    } catch {
      setSuggestions([]);
    }
  }, [canAccessPage]);
  useEffect(() => { loadSuggestions(); }, [loadSuggestions]);

  // Pull the tenant's subscription summary once on mount so the card has
  // the plan name, validity window, and live usage vs limits. Endpoint is
  // tenant-scoped server-side, so we never accidentally surface another
  // tenant's quota here.
  // Subscription is now rendered in the Topbar (Layout.jsx) — no local fetch needed.

  const executeSuggestion = async (s) => {
    setExecuting(s.product_id);
    try {
      await api.post("/inventory-transfers", {
        from_branch_id: s.from_branch_id,
        to_branch_id: s.to_branch_id,
        notes: "Smart suggestion",
        items: [{ product_id: s.product_id, qty: s.suggested_qty }],
      });
      toast.success(lang === "ar"
        ? `تم تحويل ${s.suggested_qty} من "${s.name_ar || s.name_en}"`
        : `Transferred ${s.suggested_qty} × ${s.name_en || s.name_ar}`);
      // Optimistic remove + re-fetch in the background.
      setSuggestions((prev) => prev.filter((x) => x.product_id !== s.product_id));
      loadSuggestions();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang === "ar" ? "تعذر التحويل" : "Transfer failed"));
    } finally {
      setExecuting(null);
    }
  };

  const k = data?.kpis || {};

  const calEvents = useMemo(() => {
    const out = [];
    for (const u of (data?.upcoming_bookings || [])) out.push({ date: u.date, color: "#7A1F3D" });
    for (const b of (data?.birthdays || [])) if (b.date_of_birth) {
      const dob = new Date(b.date_of_birth);
      out.push({ date: new Date(new Date().getFullYear(), dob.getMonth(), dob.getDate()).toISOString(), color: "#D4A23C" });
    }
    return out;
  }, [data]);

  const greetName = user?.name || user?.username || (lang === "ar" ? "مدير النظام" : "Manager");
  const dateLabel = new Date().toLocaleDateString(lang === "ar" ? "ar-QA" : "en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  // Today's bookings progress: confirmed vs total
  const todayBookings = data?.today_agenda?.bookings || [];
  const confirmedCount = todayBookings.filter(b => b.status === "confirmed" || b.status === "completed").length;
  const pendingCount = todayBookings.filter(b => b.status === "pending").length;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* ============== KPI strip ============== */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={Wallet}      label={lang === "ar" ? "إيرادات اليوم" : "Revenue today"}       value={fmt(k.revenue_today || 0)}    sub={`${k.invoices_today || 0} ${lang === "ar" ? "فاتورة" : "invoices"}`} color="teal"   testId="kpi-revenue-today"/>
        <KpiCard icon={CalendarDays} label={lang === "ar" ? "حجوزات اليوم" : "Bookings today"}      value={fmtInt(k.bookings_today || 0)} sub={pendingCount > 0 ? (lang === "ar" ? `${pendingCount} قيد التأكيد` : `${pendingCount} pending`) : (lang === "ar" ? "كل الحجوزات مؤكدة" : "all confirmed")} color="amber" testId="kpi-bookings-today"/>
        <KpiCard icon={UsersIcon}    label={lang === "ar" ? "العملاء النشطون" : "Active customers"} value={fmtInt(k.customers_active_week || 0)} sub={`${lang === "ar" ? "من إجمالي" : "of"} ${fmtInt(k.customers_total || 0)}`} color="violet" testId="kpi-customers-active"/>
        <KpiCard icon={PackageIcon}  label={lang === "ar" ? "تنبيهات المخزون" : "Stock alerts"}     value={fmtInt((k.low_stock || 0) + (k.out_of_stock || 0))} sub={k.out_of_stock > 0 ? (lang === "ar" ? `${k.out_of_stock} نفد` : `${k.out_of_stock} out`) : (lang === "ar" ? "بحالة جيدة" : "all good")} color={k.out_of_stock ? "rose" : "sky"} testId="kpi-low-stock"/>
      </div>

      {/* ============== Main grid ============== */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">

        {/* === LEFT (5 cols) === */}
        <div className="lg:col-span-5 space-y-5">
          {/* Quick Actions */}
          <div className="card-surface p-5">
            <SectionHead icon={Activity} color="gold" title={lang === "ar" ? "إجراءات سريعة" : "Quick Actions"} testId="qa-head"/>
            <div className="grid grid-cols-4 gap-2.5">
              <QuickAction icon={Plus}     tone="emerald" label={lang === "ar" ? "حجز" : "Booking"}     onClick={() => navigate("/bookings")} testId="qa-booking"/>
              <QuickAction icon={Receipt}  tone="sky"     label={lang === "ar" ? "فاتورة" : "Invoice"}   onClick={() => navigate("/pos")}      testId="qa-invoice"/>
              <QuickAction icon={UserPlus} tone="violet"  label={lang === "ar" ? "عميل" : "Customer"}    onClick={() => navigate("/customers")} testId="qa-customer"/>
              <QuickAction icon={FileBarChart} tone="rose" label={lang === "ar" ? "تقرير" : "Report"}    onClick={() => navigate("/reports")}  testId="qa-reports"/>
            </div>
          </div>

          {/* Now Serving — active check-in / in-service sessions */}
          <div className="card-surface p-5" data-testid="now-serving-section">
            <SectionHead icon={Scissors} color="violet"
              title={t("now_serving")}
              count={nowServing.length}
              action={(lang === "ar" ? "الحجوزات" : "Bookings") + " →"}
              onClick={() => navigate("/bookings")}/>
            <div className="space-y-3">
              {nowServing.slice(0, 6).map((row, i) => {
                const total = liveTotalMinutes(row);
                const wait = row.waiting_minutes ?? (row.service_started_at ? null : total);
                const svc = row.service_duration_minutes ?? (row.service_started_at ? total - (row.waiting_minutes ?? 0) : null);
                return (
                  <div key={row.booking_id || i} className="flex items-center gap-3 p-2.5 rounded-lg bg-violet-50/60 border border-violet-100 hover:bg-violet-50 transition-colors">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 bg-gradient-to-br from-violet-200 to-purple-200 text-violet-800 border border-violet-200">
                      {initials(row.customer_name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-[var(--text)] truncate">
                        {row.customer_name || (lang === "ar" ? "بدون اسم" : "Walk-in")}
                      </div>
                      <div className="text-[11px] text-[var(--text-muted)] truncate flex items-center gap-1.5">
                        <UserPlus size={10} className="shrink-0"/>{row.staff_name || (lang === "ar" ? "غير مُسند" : "Unassigned")}
                        <span className="opacity-40">·</span>
                        <Scissors size={10} className="shrink-0"/>{row.service_name || "—"}
                      </div>
                    </div>
                    <div className="text-end shrink-0">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                        row.session_state === "waiting" ? "bg-amber-200 text-amber-900" : "bg-violet-200 text-violet-900"
                      }`}>
                        {sessionStateLabel(row.session_state)}
                      </span>
                      <div className="text-sm font-bold gold-text num-display mt-1 flex items-center justify-end gap-1">
                        <Clock size={11}/>
                        {total} {lang === "ar" ? "د" : "min"}
                      </div>
                      {(wait != null || svc != null) && (
                        <div className="text-[10px] text-[var(--text-muted)] num-display">
                          {wait != null ? `${wait}${lang === "ar" ? "د" : "m"}` : "—"}
                          {" + "}
                          {svc != null ? `${svc}${lang === "ar" ? "د" : "m"}` : "—"}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {nowServing.length === 0 && (
                <div className="text-sm text-[var(--text-muted)] text-center py-6">
                  {lang === "ar" ? "لا يوجد عملاء قيد الخدمة حالياً" : "No active service sessions"}
                </div>
              )}
            </div>
          </div>

          {/* Pending package follow-ups */}
          <div className="card-surface p-5 border border-amber-500/20" data-testid="pending-packages-section">
            <SectionHead
              icon={PackageIcon}
              color="amber"
              title={lang === "ar" ? "باقات بانتظار المتابعة" : "Pending Package Follow-ups"}
              count={pendingPackages.length}
            />
            <div className="space-y-3">
              {pendingPackages.slice(0, 8).map((row) => {
                const pkgName = lang === "ar" ? (row.package_name_ar || row.package_name_en) : (row.package_name_en || row.package_name_ar);
                const svcName = lang === "ar" ? (row.service_name_ar || row.service_name_en) : (row.service_name_en || row.service_name_ar);
                const busy = entitlementBusy === row.entitlement_id;
                const rescheduleVal = rescheduleDates[row.entitlement_id] ?? row.scheduled_date ?? "";
                const statusLabel = row.status === "no_show"
                  ? (lang === "ar" ? "لم يحضر" : "No-show")
                  : row.is_overdue
                    ? (lang === "ar" ? "متأخر" : "Overdue")
                    : (lang === "ar" ? "معلق" : "Pending");
                const statusCls = row.status === "no_show"
                  ? "bg-stone-200 text-stone-700"
                  : row.is_overdue
                    ? "bg-rose-100 text-rose-800"
                    : "bg-amber-100 text-amber-800";
                return (
                  <div
                    key={row.entitlement_id}
                    className="rounded-lg border border-[var(--border-subtle)] p-3 space-y-2 bg-[var(--bg-alt)]/40"
                    data-testid={`pending-package-${row.entitlement_id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 text-amber-800 flex items-center justify-center font-bold shrink-0 border border-amber-200">
                        {initials(row.customer_name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-[var(--text)] truncate">{row.customer_name || (lang === "ar" ? "عميل" : "Customer")}</div>
                        <div className="text-[11px] text-[var(--text-muted)] flex flex-wrap items-center gap-x-2 gap-y-0.5">
                          {row.customer_phone && (
                            <a href={`tel:${row.customer_phone}`} className="flex items-center gap-1 hover:text-[var(--gold)]">
                              <Phone size={10}/>{row.customer_phone}
                            </a>
                          )}
                          <span className="opacity-40">·</span>
                          <span className="truncate">{pkgName}</span>
                        </div>
                        <div className="text-[11px] text-[var(--text)] mt-0.5 flex items-center gap-1.5">
                          <Scissors size={10} className="text-[var(--gold)] shrink-0"/>
                          <span className="truncate">{svcName}</span>
                        </div>
                      </div>
                      <div className="text-end shrink-0">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${statusCls}`}>{statusLabel}</span>
                        <div className="text-[10px] text-[var(--text-muted)] num-display mt-1 flex items-center justify-end gap-1">
                          <Clock size={9}/>{row.scheduled_date || "—"}
                        </div>
                        <div className="text-[9px] text-[var(--text-muted)] num-display">{row.invoice_number}</div>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-[var(--border-subtle)]">
                      <input
                        type="date"
                        className="input-dark text-[10px] py-1 px-2 flex-1 min-w-[120px]"
                        value={rescheduleVal}
                        onChange={(e) => setRescheduleDates((m) => ({ ...m, [row.entitlement_id]: e.target.value }))}
                        disabled={busy}
                      />
                      <button
                        type="button"
                        disabled={busy || !rescheduleVal}
                        onClick={() => patchEntitlement(row.entitlement_id, { status: "pending", scheduled_date: rescheduleVal })}
                        className="text-[10px] px-2 py-1 rounded border border-[var(--border)] hover:border-[var(--gold)]"
                      >
                        {lang === "ar" ? "إعادة جدولة" : "Reschedule"}
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => patchEntitlement(row.entitlement_id, { status: "no_show" })}
                        className="text-[10px] px-2 py-1 rounded border border-stone-300 text-stone-700"
                      >
                        {lang === "ar" ? "لم يحضر" : "No-show"}
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => confirmRedeemEntitlement(row.entitlement_id)}
                        className="text-[10px] px-2 py-1 rounded btn-gold"
                      >
                        {lang === "ar" ? "تمت" : "Done"}
                      </button>
                    </div>
                  </div>
                );
              })}
              {pendingPackages.length === 0 && (
                <div className="text-sm text-[var(--text-muted)] text-center py-6">
                  {lang === "ar" ? "لا توجد باقات بانتظار المتابعة" : "No pending package follow-ups"}
                </div>
              )}
            </div>
          </div>

          {/* Upcoming bookings — today (incl. completed/invoiced) + future */}
          <div className="card-surface p-5">
            <SectionHead icon={CalendarDays} color="teal"
              title={lang === "ar" ? "حجوزات اليوم والقادمة" : "Today & Upcoming"}
              count={(data?.upcoming_bookings || []).length}
              action={(lang === "ar" ? "الكل" : "All") + " " + "→"}
              onClick={() => navigate("/bookings")}/>
            <div className="space-y-3">
              {(data?.upcoming_bookings || []).slice(0, 8).map((b, i) => {
                const badge = bookingStatusBadge(b);
                const custName = (b.customer_name || "").trim() || (lang === "ar" ? "بدون اسم" : "Walk-in");
                return (
                <div key={b.booking_id || i} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-[var(--bg)] transition-colors">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 bg-gradient-to-br from-rose-100 to-pink-100 text-rose-600 border border-rose-200">
                    {initials(custName)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-[var(--text)] truncate">{custName}</div>
                    <div className="text-[11px] text-[var(--text-muted)] truncate flex items-center gap-1.5">
                      <Scissors size={10}/>{lang === "ar" ? (b.item_name_ar || b.item_name_en) : (b.item_name_en || b.item_name_ar)}
                    </div>
                  </div>
                  <div className="text-end shrink-0">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${badge.cls}`}>{badge.label}</span>
                    <div className="text-xs gold-text font-medium num-display mt-1">{b.date === data?.as_of ? (lang === "ar" ? "اليوم" : "Today") : b.date}</div>
                    <div className="text-[10px] text-[var(--text-muted)] num-display flex items-center justify-end gap-1">
                      <Clock size={9}/>{b.time_slot}
                    </div>
                  </div>
                </div>
              );})}
              {(!data?.upcoming_bookings || data.upcoming_bookings.length === 0) && (
                <div className="text-sm text-[var(--text-muted)] text-center py-6">{lang === "ar" ? "لا توجد حجوزات قادمة" : "No upcoming bookings"}</div>
              )}
            </div>
          </div>

          {/* Recent invoices feed */}
          <div className="card-surface p-5">
            <SectionHead icon={Receipt} color="indigo"
              title={lang === "ar" ? "آخر الفواتير" : "Recent Invoices"}
              count={(data?.recent_invoices || []).length}
              action={(lang === "ar" ? "الكل" : "All") + " →"}
              onClick={() => navigate("/accounting")}/>
            <div className="space-y-2">
              {(data?.recent_invoices || []).slice(0, 5).map((i, idx) => (
                <div key={i.invoice_id || idx} className="flex items-center gap-3 py-2 border-b border-[var(--border-subtle)] last:border-b-0">
                  <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center"><Receipt size={15}/></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[var(--text)] truncate flex items-center gap-2">
                      <span>{i.customer_name}</span>
                      <span className="text-[10px] text-[var(--text-muted)] num-display">{i.invoice_number}</span>
                    </div>
                    <div className="text-[10px] text-[var(--text-muted)]">{timeAgo(i.created_at, lang)} · {i.items_count} {lang === "ar" ? "بند" : "items"}</div>
                  </div>
                  <div className="gold-text font-bold num-display text-sm shrink-0">{fmt(i.total)}</div>
                </div>
              ))}
              {(!data?.recent_invoices || data.recent_invoices.length === 0) && (
                <div className="text-sm text-[var(--text-muted)] text-center py-4">{lang === "ar" ? "لا توجد فواتير بعد" : "No invoices yet"}</div>
              )}
            </div>
          </div>
        </div>

        {/* === MIDDLE (4 cols) === */}
        <div className="lg:col-span-4 space-y-5">
          {/* Top services today */}
          <div className="card-surface p-5">
            <SectionHead icon={Sparkles} color="rose"
              title={lang === "ar" ? "أعلى الخدمات اليوم" : "Top Services Today"}
              count={(data?.top_services_today || []).length}/>
            <div className="space-y-3">
              {(data?.top_services_today || []).map((s, i) => {
                const max = (data?.top_services_today?.[0]?.count) || 1;
                const pct = Math.round((s.count / max) * 100);
                return (
                  <div key={s.service_id || i} className="space-y-1.5">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-7 h-7 rounded-md bg-rose-50 text-rose-600 flex items-center justify-center shrink-0 text-xs font-bold">{i + 1}</div>
                        <span className="text-[var(--text)] font-medium truncate">{lang === "ar" ? s.name_ar : s.name_en}</span>
                      </div>
                      <div className="shrink-0 flex items-center gap-2">
                        <span className="text-xs text-[var(--text-muted)] num-display">{s.count}×</span>
                        <span className="gold-text font-bold text-sm num-display">{fmt(s.revenue)}</span>
                      </div>
                    </div>
                    <div className="h-1.5 bg-rose-50 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-rose-400 to-rose-600 rounded-full transition-all" style={{ width: `${pct}%` }}/>
                    </div>
                  </div>
                );
              })}
              {(!data?.top_services_today || data.top_services_today.length === 0) && (
                <div className="text-sm text-[var(--text-muted)] text-center py-6 flex flex-col items-center gap-2">
                  <Sparkles size={20} className="opacity-40"/>
                  <span>{lang === "ar" ? "لم تُحجز أي خدمة اليوم بعد" : "No services booked today yet"}</span>
                </div>
              )}
            </div>
          </div>

          {/* VIP customers */}
          <div className="card-surface p-5">
            <SectionHead icon={Crown} color="gold"
              title={lang === "ar" ? "كبار العملاء" : "VIP Customers"}
              count={(data?.vip_customers || []).length}
              action={(lang === "ar" ? "الكل" : "All") + " →"}
              onClick={() => navigate("/customers")}/>
            <div className="space-y-2">
              {(data?.vip_customers || []).map((c, i) => (
                <div key={c.customer_id || i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[var(--bg)] transition-colors">
                  <div className="relative">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 text-white flex items-center justify-center font-bold shadow-sm">
                      {initials(c.name)}
                    </div>
                    {i === 0 && (
                      <div className="absolute -top-1 -end-1 w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center text-white shadow-md">
                        <Crown size={10}/>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[var(--text)] truncate">{c.name}</div>
                    <div className="text-[10px] text-[var(--text-muted)] flex items-center gap-2 num-display">
                      {c.phone && <span className="flex items-center gap-0.5"><Phone size={8}/>{c.phone}</span>}
                      <span>{c.visits} {lang === "ar" ? "زيارة" : "visits"}</span>
                    </div>
                  </div>
                  <div className="text-end shrink-0">
                    <div className="gold-text font-bold text-sm num-display">{fmt(c.total_spent)}</div>
                  </div>
                </div>
              ))}
              {(!data?.vip_customers || data.vip_customers.length === 0) && (
                <div className="text-sm text-[var(--text-muted)] text-center py-4">{lang === "ar" ? "لم يُسجّل عملاء بعد" : "No customers yet"}</div>
              )}
            </div>
          </div>

          {/* Top staff this month */}
          <div className="card-surface p-5">
            <SectionHead icon={Trophy} color="amber"
              title={lang === "ar" ? "أفضل الموظفات هذا الشهر" : "Top Staff This Month"}
              count={(data?.top_staff_month || []).length}/>
            <div className="space-y-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-[var(--text-muted)] mb-2">
                  {lang === "ar" ? "الإيراد" : "Revenue"}
                </div>
                <TopStaffRankRows
                  list={data?.top_staff_month}
                  mode="revenue"
                  lang={lang}
                  fmt={fmt}
                  emptyMsg={lang === "ar" ? "لا يوجد نشاط مُعيّن لموظفات" : "No staff-assigned activity"}
                />
              </div>
              <div className="border-t border-[var(--border-subtle)] pt-4">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-[var(--text-muted)] mb-2">
                  {lang === "ar" ? "الحجوزات" : "Bookings"}
                </div>
                <TopStaffRankRows
                  list={data?.top_staff_month_bookings || []}
                  mode="bookings"
                  lang={lang}
                  fmt={fmt}
                  emptyMsg={lang === "ar" ? "لا يوجد نشاط مُعيّن لموظفات" : "No staff-assigned activity"}
                />
              </div>
            </div>
          </div>
        </div>

        {/* === RIGHT (3 cols) === */}
        <div className="lg:col-span-3 space-y-5">
          {/* Revenue trend */}
          <div className="card-surface p-5">
            <SectionHead icon={TrendingUp} color="violet" title={lang === "ar" ? "آخر 7 أيام" : "Last 7 days"}/>
            <div className="text-center mb-2">
              <div className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">{lang === "ar" ? "إيرادات الشهر" : "Month revenue"}</div>
              <div className="font-heading text-2xl gold-text num-display">{fmt(k.revenue_month || 0)}</div>
            </div>
            <ResponsiveContainer width="100%" height={110}>
              <AreaChart data={data?.trend_7d || []} margin={{ top: 5, right: 5, left: 5, bottom: 0 }}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"  stopColor="#7A1F3D" stopOpacity={0.5}/>
                    <stop offset="100%" stopColor="#7A1F3D" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)"/>
                <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={(v) => v.slice(8, 10)}/>
                <Tooltip formatter={(v) => fmt(v)}/>
                <Area type="monotone" dataKey="revenue" stroke="#7A1F3D" strokeWidth={2} fill="url(#rev)"/>
              </AreaChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-2 gap-2 mt-2 text-center text-[11px]">
              <div className="bg-rose-50 rounded-md py-1.5">
                <div className="text-rose-700 font-bold num-display">{(data?.trend_7d || []).reduce((s, d) => s + (d.bookings || 0), 0)}</div>
                <div className="text-[9px] text-[var(--text-muted)]">{lang === "ar" ? "حجوزات" : "Bookings"}</div>
              </div>
              <div className="bg-emerald-50 rounded-md py-1.5">
                <div className="text-emerald-700 font-bold num-display">{fmt((data?.trend_7d || []).reduce((s, d) => s + (d.revenue || 0), 0))}</div>
                <div className="text-[9px] text-[var(--text-muted)]">{lang === "ar" ? "إيرادات" : "Revenue"}</div>
              </div>
            </div>
          </div>

          {/* Mini Calendar */}
          <MiniCalendar events={calEvents} lang={lang}/>

          {/* Birthdays */}
          <div className="card-surface p-5">
            <SectionHead icon={Cake} color="pink"
              title={lang === "ar" ? "أعياد ميلاد الشهر" : "Birthdays"}
              count={(data?.birthdays || []).length}/>
            <div className="space-y-2">
              {(data?.birthdays || []).slice(0, 4).map((b, i) => (
                <div key={b.customer_id || i} className="flex items-center gap-3 py-1.5">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-200 to-pink-400 text-white flex items-center justify-center shadow-sm">
                    <Cake size={14}/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-[var(--text)] truncate">{b.name}</div>
                    <div className="text-[10px] text-[var(--text-muted)] num-display">{b.date_of_birth}</div>
                  </div>
                </div>
              ))}
              {(!data?.birthdays || data.birthdays.length === 0) && (
                <div className="text-sm text-[var(--text-muted)] text-center py-3">{lang === "ar" ? "لا توجد أعياد ميلاد هذا الشهر" : "No birthdays this month"}</div>
              )}
            </div>
          </div>

          {/* Stock alerts */}
          <div className="card-surface p-5">
            <SectionHead icon={AlertTriangle} color="amber"
              title={lang === "ar" ? "تنبيهات المخزون" : "Stock Alerts"}
              count={(k.low_stock || 0) + (k.out_of_stock || 0)}
              action={(lang === "ar" ? "كل المنتجات" : "All") + " →"}
              onClick={() => navigate("/products")}/>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="bg-gradient-to-br from-rose-50 to-rose-100 border border-rose-200 rounded-xl p-3 text-center">
                <div className="text-2xl font-bold text-rose-600 num-display">{k.out_of_stock || 0}</div>
                <div className="text-[10px] text-rose-700 uppercase tracking-wider mt-0.5">{lang === "ar" ? "نفد" : "Out"}</div>
              </div>
              <div className="bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200 rounded-xl p-3 text-center">
                <div className="text-2xl font-bold text-amber-600 num-display">{k.low_stock || 0}</div>
                <div className="text-[10px] text-amber-700 uppercase tracking-wider mt-0.5">{lang === "ar" ? "منخفض" : "Low"}</div>
              </div>
            </div>
            <div className="space-y-1.5">
              {(data?.low_stock_list || []).slice(0, 3).map(p => (
                <div key={p.product_id} className="flex items-center justify-between text-xs border-b border-[var(--border-subtle)] py-1.5 last:border-b-0">
                  <span className="text-[var(--text)] truncate flex-1 flex items-center gap-1.5">
                    <PackageIcon size={11} className="text-[var(--text-muted)]"/>
                    {lang === "ar" ? p.name_ar : p.name_en}
                  </span>
                  <span className={`badge ${p.out ? "badge-red" : "badge-warn"} num-display text-[10px]`}>{p.stock}/{p.threshold}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Smart Transfer Suggestions — FULL WIDTH below the main grid */}
      {suggestions.length > 0 && (
        <div className="card-surface p-5" data-testid="smart-transfer-suggestions">
          <SectionHead icon={Zap} color="violet"
            title={lang === "ar" ? "اقتراحات تحويل ذكية" : "Smart Transfer Suggestions"}
            count={suggestions.length}
            action={(lang === "ar" ? "كل التحويلات" : "All transfers") + " →"}
            onClick={() => navigate("/inventory-transfers")}
            testId="smart-suggest-title"/>
          <p className="text-xs text-[var(--text-muted)] -mt-2 mb-4 leading-relaxed">
            {lang === "ar"
              ? "منتجات منخفضة في فرع لديك فائض منها في فرع آخر — حوّل بنقرة واحدة."
              : "Stock low at one branch but surplus at another — rebalance with one click."}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {suggestions.slice(0, 6).map((s) => {
              const isExec = executing === s.product_id;
              return (
                <div key={s.product_id}
                     className="border border-[var(--border-subtle)] rounded-xl p-4 hover:border-[var(--gold)] hover:bg-[var(--gold-tint)] transition"
                     data-testid={`transfer-suggestion-${s.product_id}`}>
                  {/* Product header */}
                  <div className="flex items-start justify-between gap-2 mb-3 pb-3 border-b border-[var(--border-subtle)]">
                    <div className="min-w-0 flex-1">
                      <div className="text-base font-bold text-[var(--text)] flex items-center gap-1.5 leading-tight">
                        <PackageIcon size={14} className="text-[var(--gold)] flex-shrink-0"/>
                        <span className="truncate">{lang === "ar" ? s.name_ar : s.name_en}</span>
                      </div>
                      {s.sku && <div className="text-[11px] text-[var(--text-muted)] font-mono mt-0.5 ps-5">{s.sku}</div>}
                    </div>
                    {s.to_qty === 0 && (
                      <span className="badge badge-red text-[10px] px-2 py-0.5 whitespace-nowrap font-bold">
                        {lang === "ar" ? "نفد" : "OUT"}
                      </span>
                    )}
                  </div>

                  {/* From → To (vertical stack) */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <Building2 size={13} className="text-rose-600 flex-shrink-0"/>
                        <span className="text-sm text-rose-900 font-medium truncate">{lang === "ar" ? s.from_branch_name_ar : s.from_branch_name_en}</span>
                      </div>
                      <div className="text-xs text-rose-700 font-bold num-display whitespace-nowrap">
                        {s.from_qty} {lang === "ar" ? "متوفر" : "in stock"}
                      </div>
                    </div>
                    <div className="flex justify-center">
                      <div className="w-7 h-7 rounded-full bg-[var(--gold)] text-white flex items-center justify-center shadow-sm">
                        <ArrowRightLeft size={13}/>
                      </div>
                    </div>
                    <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <Building2 size={13} className="text-emerald-600 flex-shrink-0"/>
                        <span className="text-sm text-emerald-900 font-medium truncate">{lang === "ar" ? s.to_branch_name_ar : s.to_branch_name_en}</span>
                      </div>
                      <div className="text-xs text-emerald-700 font-bold num-display whitespace-nowrap">
                        {s.to_qty} {lang === "ar" ? "متبقي" : "left"}
                      </div>
                    </div>
                  </div>

                  {/* Suggested qty + action */}
                  <div className="flex items-center justify-between gap-3 pt-1">
                    <div className="text-sm whitespace-nowrap">
                      <span className="text-[var(--text-muted)]">{lang === "ar" ? "المقترح: " : "Suggested: "}</span>
                      <span className="font-bold gold-text num-display text-base">×{s.suggested_qty}</span>
                    </div>
                    <button
                      onClick={() => executeSuggestion(s)}
                      disabled={isExec}
                      data-testid={`execute-suggestion-${s.product_id}`}
                      className="btn-gold px-4 py-2 rounded-md text-sm flex items-center gap-1.5 disabled:opacity-50 whitespace-nowrap font-medium"
                    >
                      <Zap size={13}/>
                      {isExec
                        ? (lang === "ar" ? "جارٍ..." : "...")
                        : (lang === "ar" ? "نفّذ التحويل" : "Transfer now")}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {loading && <div className="text-center text-xs text-[var(--text-muted)]">{lang === "ar" ? "جارٍ التحميل..." : "Loading..."}</div>}
      <ConfirmDialogSlot />
    </div>
  );
}
